import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../../components/Header";
import { MapaAtasHeader } from "./components/MapaAtasHeader";
import { MapaAtasGrid } from "./components/MapaAtasGrid";
import { MapaAtasPreview } from "./components/MapaAtasPreview";
import { useMapaAtas } from "../../hooks/useMapaAtas";
import { Card, CardContent } from "../../components/ui/card";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import * as customizationService from "../../services/customization.service";
import { Skeleton } from "../../components/ui/skeleton";
import type { ExportLanguage } from "./components/ExportLanguageDialog";
import { translateMapaAtasData } from "../../services/translation.service";
import type { SupportedLanguage } from "../../i18n/config";
import type { MapaAtasResponse } from "../../../types/database";
import { usePageTitle } from "../../hooks/usePageTitle";

export default function MapaAtas() {
  const { t, i18n } = useTranslation('common');
  const { id: projetoIdParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<{ current: number; total: number } | null>(null);
  const [translatedData, setTranslatedData] = useState<MapaAtasResponse | null>(null);

  const projetoId = projetoIdParam ? parseInt(projetoIdParam, 10) : 0;

  const { data, loading, error, refetch } = useMapaAtas({
    projetoId,
    autoLoad: true,
  });
  usePageTitle(data ? `Mapa de Atas — ${data.projeto.nome}` : 'Mapa de Atas');


  useEffect(() => {
    const fetchCustomization = async () => {
      try {
        const customization = await customizationService.getUserCustomization();
        setHeaderImageUrl(customization.header_image_url);
      } catch (error) {
        console.error("Erro ao carregar personalização:", error);
      }
    };

    fetchCustomization();
  }, []);

  const handleExportPDF = async (language: ExportLanguage) => {
    const originalTitle = document.title;
    const originalLanguage = i18n.language as SupportedLanguage;
    const needsTranslation = language !== originalLanguage;

    if (needsTranslation && data) {
      setIsTranslating(true);
      try {
        const translated = await translateMapaAtasData(
          data,
          originalLanguage,
          language as SupportedLanguage,
          (current, total) => setTranslationProgress({ current, total })
        );
        setTranslatedData(translated);
        await i18n.changeLanguage(language);
      } catch (err) {
        console.error('Falha na tradução, prosseguindo sem traduzir:', err);
        await i18n.changeLanguage(language);
      }
      setIsTranslating(false);
      setTranslationProgress(null);
    } else {
      i18n.changeLanguage(language);
    }

    document.title = " ";

    // Aguarda a mudança de idioma antes de imprimir
    setTimeout(() => {
      const html = document.documentElement;
      const wasDark = html.classList.contains('dark');
      if (wasDark) html.classList.remove('dark');

      window.print();

      // Restaura o idioma original após a impressão
      setTimeout(() => {
        if (wasDark) html.classList.add('dark');
        document.title = originalTitle;
        i18n.changeLanguage(originalLanguage);
        setTranslatedData(null);
      }, 1000);
    }, 100);
  };


  if (loading) {
    return (
      <>
        <Header />
        <div className="bg-white border-b border-border">
          <div className="container mx-auto px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-24 rounded-md" />
              <div className="h-8 w-px bg-border" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="h-10 w-32 rounded-md" />
            </div>
          </div>
        </div>
        <div className="min-h-screen bg-muted/30">
          <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg border border-border shadow-sm overflow-hidden p-6">
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }


  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-md border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-900 mb-2">
                    {t('mapaAtas.error_loading')}
                  </p>
                  <p className="text-sm text-red-800 mb-4">{error}</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => refetch()}>
                      {t('mapaAtas.try_again')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/projects/${projetoId}/meetings`)}
                    >
                      {t('mapaAtas.back_to_project')}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }


  if (!data) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-md">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-2">
                    {t('mapaAtas.no_data')}
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('mapaAtas.no_meetings')}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/projects/${projetoId}/meetings`)}
                  >
                    {t('mapaAtas.back_to_project')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }


  return (
    <>
      {}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm;
          }

          .print-mapa-atas * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
          }

          body {
            font-size: 7pt;
            line-height: 1.2;
          }

          .no-print {
            display: none !important;
          }

          
          .container {
            max-width: 100% !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          
          .print-mapa-atas h1 {
            font-size: 12pt;
            margin: 0 0 4pt 0;
          }

          .print-mapa-atas h2 {
            font-size: 10pt;
            margin: 3pt 0;
          }

          .print-mapa-atas p,
          .print-mapa-atas td,
          .print-mapa-atas th,
          .print-mapa-atas span,
          .print-mapa-atas div {
            font-size: 6.5pt;
            line-height: 1.2;
          }

          
          .print-mapa-atas table {
            width: 100% !important;
            border-collapse: collapse;
            font-size: 6.5pt;
            table-layout: auto !important;
          }

          .print-mapa-atas th {
            padding: 2pt 1.5pt;
            font-size: 6pt;
            font-weight: bold;
            background-color: #f3f4f6 !important;
            border: 0.5pt solid #d1d5db;
            text-align: center;
            white-space: nowrap;
          }

          .print-mapa-atas td {
            padding: 2pt 1.5pt;
            font-size: 6.5pt;
            border: 0.5pt solid #d1d5db;
          }

          
          .print-mapa-atas td:first-child,
          .print-mapa-atas td:nth-child(n+3) {
            white-space: nowrap;
          }

          
          .print-mapa-atas td:nth-child(2) {
            word-wrap: break-word;
            overflow-wrap: break-word;
            max-width: 200pt;
          }

          
          .print-mapa-atas .bg-green-100 {
            background-color: #dcfce7 !important;
          }

          .print-mapa-atas .bg-yellow-100 {
            background-color: #fef3c7 !important;
          }

          .print-mapa-atas .bg-red-100 {
            background-color: #fee2e2 !important;
          }

          .print-mapa-atas .bg-gray-50,
          .print-mapa-atas .bg-gray-100,
          .print-mapa-atas .bg-gray-200 {
            background-color: #f9fafb !important;
          }

          
          .print-mapa-atas .p-4,
          .print-mapa-atas .p-6 {
            padding: 3pt !important;
          }

          .print-mapa-atas .mb-4,
          .print-mapa-atas .mb-6 {
            margin-bottom: 3pt !important;
          }

          
          .print-mapa-atas .shadow-sm {
            box-shadow: none !important;
          }

          
          .print-mapa-atas img {
            max-height: 30pt;
            width: auto;
          }

          
          .print-mapa-atas .page-break-inside-avoid {
            page-break-inside: avoid;
          }

          
          .print-mapa-atas .border-2 {
            border-width: 1pt !important;
          }

          .print-mapa-atas .border-gray-800 {
            border-color: #1f2937 !important;
          }

          .print-mapa-atas .border-gray-600 {
            border-color: #4b5563 !important;
          }
        }
      `}</style>
      
      <div className="no-print">
        <Header />
        <MapaAtasHeader projeto={data.projeto} onExportPDF={handleExportPDF} />
      </div>

      <div className="min-h-screen bg-muted/30 print:bg-white print:min-h-0">
        <div className="container mx-auto px-4 sm:px-6 py-6 print:p-0">
          {/* Grid view (tela) */}
          <div className="bg-white rounded-lg border border-border shadow-sm overflow-hidden print:hidden">
            <MapaAtasGrid data={data} />
          </div>

          {/* Preview para impressão */}
          <div className="hidden print:block print-mapa-atas">
            <MapaAtasPreview data={translatedData || data} />
          </div>
        </div>
      </div>

      {/* Overlay de tradução */}
      {isTranslating && (
        <div className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center no-print">
          <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-xl shadow-lg border border-border" style={{ minWidth: 240 }}>
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="text-sm font-medium text-gray-700">Traduzindo conteúdo...</p>
            {translationProgress && translationProgress.total > 0 ? (
              <>
                <p className="text-xs text-gray-500">
                  {translationProgress.current} / {translationProgress.total} campos
                </p>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((translationProgress.current / translationProgress.total) * 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-400">Isso pode levar alguns segundos</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
